import api from './api';
import {
  MedicalAppointment,
  MedicalAppointmentType,
  MedicalAppointmentCategory,
  MedicalAppointmentStatus,
  MedicalCompleteResponse,
} from '@/types';

// ---- Interfaces para crear/actualizar tipos ----

export interface MedicalAppointmentTypeCreate {
  name: string;
  category: MedicalAppointmentCategory;
  description?: string;
  default_duration_minutes?: number;
  default_pre_op_notes?: string;
}

export interface MedicalAppointmentTypeUpdate {
  name?: string;
  category?: MedicalAppointmentCategory;
  description?: string;
  default_duration_minutes?: number;
  default_pre_op_notes?: string;
  is_active?: boolean;
}

// ---- Interfaces para crear/actualizar turnos ----

export interface MedicalAppointmentCreate {
  patient_id: number;
  appointment_type_id?: number;
  category: MedicalAppointmentCategory;
  scheduled_date: string;
  scheduled_time: string;
  duration_minutes?: number;
  pre_op_notes?: string;
  notes?: string;
}

export interface MedicalAppointmentUpdate {
  patient_id?: number;
  appointment_type_id?: number;
  category?: MedicalAppointmentCategory;
  scheduled_date?: string;
  scheduled_time?: string;
  duration_minutes?: number;
  status?: MedicalAppointmentStatus;
  pre_op_notes?: string;
  notes?: string;
}

export interface MedicalFilters {
  date_from?: string;
  date_to?: string;
  category?: MedicalAppointmentCategory;
  status?: MedicalAppointmentStatus;
  patient_id?: number;
  skip?: number;
  limit?: number;
}

// ---- Servicio API ----

export const medicalService = {
  // Tipos de procedimiento
  async getTypes(category?: string, activeOnly?: boolean): Promise<MedicalAppointmentType[]> {
    const response = await api.get<MedicalAppointmentType[]>('/medical-appointments/types', {
      params: { category, active_only: activeOnly },
    });
    return response.data;
  },

  async createType(data: MedicalAppointmentTypeCreate): Promise<MedicalAppointmentType> {
    const response = await api.post<MedicalAppointmentType>('/medical-appointments/types', data);
    return response.data;
  },

  async updateType(id: number, data: MedicalAppointmentTypeUpdate): Promise<MedicalAppointmentType> {
    const response = await api.put<MedicalAppointmentType>(`/medical-appointments/types/${id}`, data);
    return response.data;
  },

  async deleteType(id: number): Promise<void> {
    await api.delete(`/medical-appointments/types/${id}`);
  },

  // Turnos
  async getAppointments(filters?: MedicalFilters): Promise<MedicalAppointment[]> {
    const response = await api.get<MedicalAppointment[]>('/medical-appointments', { params: filters });
    return response.data;
  },

  async getCalendar(dateFrom: string, dateTo: string, category?: string): Promise<MedicalAppointment[]> {
    const response = await api.get<MedicalAppointment[]>('/medical-appointments/calendar', {
      params: { date_from: dateFrom, date_to: dateTo, category },
    });
    return response.data;
  },

  async getAppointment(id: number): Promise<MedicalAppointment> {
    const response = await api.get<MedicalAppointment>(`/medical-appointments/${id}`);
    return response.data;
  },

  async createAppointment(data: MedicalAppointmentCreate): Promise<MedicalAppointment> {
    const response = await api.post<MedicalAppointment>('/medical-appointments', data);
    return response.data;
  },

  async updateAppointment(id: number, data: MedicalAppointmentUpdate): Promise<MedicalAppointment> {
    const response = await api.put<MedicalAppointment>(`/medical-appointments/${id}`, data);
    return response.data;
  },

  async completeAppointment(id: number): Promise<MedicalCompleteResponse> {
    const response = await api.post<MedicalCompleteResponse>(`/medical-appointments/${id}/complete`);
    return response.data;
  },

  async cancelAppointment(id: number): Promise<MedicalAppointment> {
    const response = await api.post<MedicalAppointment>(`/medical-appointments/${id}/cancel`);
    return response.data;
  },

  async deleteAppointment(id: number): Promise<void> {
    await api.delete(`/medical-appointments/${id}`);
  },
};

// ---- Labels para UI ----

export const CATEGORY_LABELS: Record<MedicalAppointmentCategory, string> = {
  surgery: 'Cirugía',
  ultrasound: 'Ecografía',
};

export const STATUS_LABELS: Record<MedicalAppointmentStatus, string> = {
  pending: 'Pendiente',
  completed: 'Completado',
  cancelled: 'Cancelado',
};

export const STATUS_COLORS: Record<MedicalAppointmentStatus, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

export const CATEGORY_COLORS: Record<MedicalAppointmentCategory, string> = {
  surgery: 'bg-red-100 text-red-800',
  ultrasound: 'bg-purple-100 text-purple-800',
};
